import { useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Skeleton, Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useIamRole,
  useRolePolicyMutation,
  s3ExtractError,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../../S3ConsoleBreadcrumbs';
import { PolicyAttachments } from '../PolicyAttachments';
import { JsonEditor } from '@/components/JsonEditor';

/** IAM role detail — read-only trust policy and attached policies (attach/detach). */
export function RoleDetail({ serviceId }: { serviceId: string }) {
  const toast = useToast();
  const { name = '' } = useParams<{ name: string }>();
  const roleName = decodeURIComponent(name);
  const { data, isLoading } = useIamRole(serviceId, roleName);
  const attach = useRolePolicyMutation(serviceId, true);
  const detach = useRolePolicyMutation(serviceId, false);

  const runPolicy = (m: typeof attach, policyArn: string, ok: string, fail: string) =>
    m.mutate(
      { name: roleName, policyArn },
      { onSuccess: () => toast.success(ok), onError: (e) => toast.error(s3ExtractError(e, fail)) },
    );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <S3ConsoleBreadcrumbs trail={[{ label: 'Roles', slug: 'iam/roles' }, { label: roleName }]} />
        }
        title={roleName}
        description="IAM role trust policy and attached policies."
      />

      {isLoading ? (
        <Skeleton variant="rect" height={320} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Trust policy</CardTitle>
            </CardHeader>
            <CardBody>
              <JsonEditor
                value={data?.assume_role_policy_document ?? ''}
                readOnly
                ariaLabel="Trust policy"
              />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Attached policies</CardTitle>
            </CardHeader>
            <CardBody>
              <PolicyAttachments
                serviceId={serviceId}
                attached={data?.attached_policies ?? []}
                pending={attach.isPending || detach.isPending}
                onAttach={(arn) => runPolicy(attach, arn, 'Policy attached', 'Failed to attach policy')}
                onDetach={(arn) => runPolicy(detach, arn, 'Policy detached', 'Failed to detach policy')}
              />
            </CardBody>
          </Card>
        </div>
      )}
    </Container>
  );
}
